// @ts-check

import { compareTerms, pickBestTerm, getBaseSnapshot, projectedNOIAtYear, projectedValueAtYear } from "./termComparator";
import { mortgageBalanceAfterYears, annualDebtService } from "./amortization";
import { num, round2 } from "./validators";

/**
 * Projection annuelle d'une propriété.
 * MVP: pas de refinancement automatique; on calcule la trajectoire "tel quel".
 */
export function projectPropertyAnnual({ inputs, cfg, assumptions, horizonYears, objective }) {
  const base = getBaseSnapshot(inputs);

  const purchasePrice = num(inputs.purchasePrice, 0);
  const mortgageAmount = num(inputs.mortgageAmount, 0);
  const amortizationYears = num(inputs.amortizationYears, 25);

  const rows = compareTerms({ inputs, cfg, assumptions });
  const best = pickBestTerm(rows, objective) || rows[0];
  const bestTerm = best?.termYears || 5;
  const contractRate = num(inputs?.ratesByTerm?.[bestTerm], 0);

  const points = [];

  for (let y = 0; y <= horizonYears; y++) {
    const value = projectedValueAtYear(purchasePrice, y, num(assumptions?.appreciationAnnual, 0));
    const noi = projectedNOIAtYear({ base, yearsElapsed: y, assumptions, cfg });

    const bal = mortgageBalanceAfterYears({
      principal: mortgageAmount,
      ratePct: contractRate,
      amortizationYears,
      yearsElapsed: y,
      paymentFrequency: "monthly"
    });

    const debtSvc = annualDebtService({
      principal: mortgageAmount,
      ratePct: contractRate,
      amortizationYears,
      paymentFrequency: "monthly"
    });

    const cashflow = round2(noi - debtSvc);
    const dscr = debtSvc > 0 ? round2(noi / debtSvc) : null;

    points.push({
      yearIndex: y,
      propertyValue: value,
      mortgageBalance: bal,
      equity: round2(value - bal),
      noi,
      annualDebtService: debtSvc,
      cashflowAnnual: cashflow,
      dscr
    });
  }

  return {
    points,
    bestTerm,
    nextOpportunity: best
  };
}

export function projectPortfolioAnnual({ properties, cfg, horizonYears, objective }) {
  const perProp = properties.map((p) => {
    const r = projectPropertyAnnual({
      inputs: p.inputs,
      cfg,
      assumptions: p.assumptions,
      horizonYears,
      objective
    });
    return { id: p.id, label: p.inputs?.label || "Propriété", ...r };
  });

  const series = [];
  for (let y = 0; y <= horizonYears; y++) {
    let valueTotal = 0;
    let debtTotal = 0;
    let noiTotal = 0;
    let debtSvcTotal = 0;
    let cashflowTotal = 0;

    for (const p of perProp) {
      const pt = p.points[y];
      if (!pt) continue;
      valueTotal += pt.propertyValue;
      debtTotal += pt.mortgageBalance;
      noiTotal += pt.noi;
      debtSvcTotal += pt.annualDebtService;
      cashflowTotal += pt.cashflowAnnual;
    }

    const dscrGlobal = debtSvcTotal > 0 ? round2(noiTotal / debtSvcTotal) : null;

    series.push({
      yearIndex: y,
      valueTotal: round2(valueTotal),
      debtTotal: round2(debtTotal),
      equityTotal: round2(valueTotal - debtTotal),
      cashflowTotal: round2(cashflowTotal),
      dscrGlobal
    });
  }

  const opportunities = perProp.map((p) => ({
    propertyId: p.id,
    label: p.label,
    renewalDateISO: p.nextOpportunity?.renewalDateISO,
    bestTerm: p.bestTerm,
    cashOutMax: p.nextOpportunity?.cashOutMax,
    status: p.nextOpportunity?.status,
    reasons: p.nextOpportunity?.reasons || []
  }));

  return { series, opportunities };
}
